import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    InputLabel,
    Stack,
    useTheme,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import React, { useEffect, useState } from 'react';
import {
    FormProvider,
    useForm,
    useFormContext,
    useWatch,
} from 'react-hook-form';
import { z } from 'zod';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import AutoHeightTransition from '../../../../shared/AutoHeightTransition';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import { ExperienceType, SpaceType } from '../../../enums/experience';
import FormSelect from '../../../shared/FormSelect';
import FormSwitch from '../../../shared/FormSwitch';
import { FormValues, Space } from '../../../types/experience';
import { ModalProps } from '../../../types/modal';
import BathroomForm from './BathroomForm';
import BedroomForm from './BedroomForm';
import BoardRoomForm from './BoardRoomForm';
import DiningAreaForm from './DiningAreaForm';
import GameRoomForm from './GameRoomForm';
import GarageForm from './GarageForm';
import { ModifiedSpace } from './IndividualSpace';
import KitchenForm from './KithcenForm';
import LivingRoomForm from './LivingRoomForm';
import PrivateOfficeForm from './PrivateOfficeForm';
import SpaceAvailability from './SpaceAvailability';
import { SpaceSchema } from '../Form/Amenities/schemas/spaces.schema';

interface Props extends ModalProps {
    type?: string;
    subType?: string;
    onSubmit: (data: Space) => void;
    existingSpace: ModifiedSpace | null;
}

const SpaceModal = ({
    open,
    onClose,
    onSubmit,
    type,
    subType,
    existingSpace,
}: Props) => {
    const theme = useTheme();
    const [showAvailability, setShowAvailability] = useState(false);

    const defaultValues = {
        spaceType:
            type === ExperienceType.Commercial
                ? SpaceType.Boardroom
                : SpaceType.Bedroom,
        cleaningFee: 0,
        singleBed: 0,
        doubleBed: 0,
        queenBed: 0,
        kingBed: 0,
        bunkBed: 0,
        sofaBed: 0,
        couch: 0,
        babyCot: 0,
        blackOutCurtains: false,
        wardrobe: false,
        extraPillowsAndBlankets: false,
        iron: false,
        safe: false,
        tv: false,
        ensuite: false,
        desk: 0,
        bathtub: false,
        babyBath: false,
        hairdryer: false,
        heating: false,
        bidet: false,
        toiletries: false,
        seatingCapacity: 0,
        projector: false,
        whiteboard: false,
        soundSystem: false,
        gameConsole: false,
        poolTable: false,
        fridge: false,
        oven: false,
        microwave: false,
        dishwasher: false,
        coffeeMachine: false,
        toaster: false,
        kettle: false,
        cookware: false,
        cutlery: false,
        fireplace: false,
        parkingCapacity: 0,
        evChargingStation: false,
        securitySystem: false,
        automaticDoor: false,
        dehumidifier: false,
        carwashStation: false,
        bicycleRack: false,
        description: '',
        outletsAndChargers: false,
        transmittingEquipment: false,
        camera: false,
        zoom: false,
        msTeams: false,
        googleMeet: false,
        microphone: false,
        printingAndScanning: false,
        monitor: false,
        keyboard: false,
        mouse: false,
        dockingStation: false,
        ergonomicChair: false,
        adjustabledDesk: false,
        bed: false,
        dedicatedDesk: false,
        closet: false,
        bathroom: false,
        cableAdapters: false,
        isAvailable: true,
        unavailablePeriods: [],
        assignedName: '',
        sleepingCapacity: 0,
    };

    const methods = useForm<Space>({
        defaultValues,
        resolver: zodResolver(
            SpaceSchema.superRefine((val, ctx) => {
                if (type === ExperienceType.Commercial) {
                    const commercialVal = val as {
                        assignedName?: string;
                    };

                    if (!commercialVal.assignedName) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: ['assignedName'],
                        });
                    }
                }
            }),
        ),
        // shouldUnregister: true,
    });

    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { isDirty, dirtyFields },
    } = methods;

    const isAvailable = useWatch({ control, name: 'isAvailable' });

    const spaceType = watch('spaceType');
    const [cancel, setCancel] = useState<boolean>(false);

    const handleCancel = () => {
        if (isDirty) {
            // if form is dirty, display alert
            if (!cancel) {
                setCancel(true);
                return;
            }
        }
        if (cancel) reset();
        setCancel(false);
        handleClose();
    };

    const onNestedSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
        if (event && typeof event.stopPropagation === 'function') {
            event.stopPropagation();
        }

        return handleSubmit(onSubmit)(event);
    };

    useEffect(() => {
        if (open && !existingSpace) {
            const newDefaultValues = { ...defaultValues, spaceType: spaceType };
            reset(newDefaultValues);
        }
    }, [spaceType]);

    const [isFormDirty, setIsFormDirty] = useState(false);

    const formDirtyFields = Object.keys(dirtyFields);

    useEffect(() => {
        if (isDirty) {
            if (
                formDirtyFields.length === 1 &&
                formDirtyFields.includes('spaceType')
            ) {
                setIsFormDirty(false);
            } else {
                setIsFormDirty(true);
            }
        } else {
            setIsFormDirty(false);
        }
    }, [formDirtyFields]);

    useEffect(() => {
        if (open) {
            if (existingSpace) {
                reset(existingSpace);
            } else reset(defaultValues);
        }
    }, [existingSpace, reset, open]);

    const typeOptions =
        type === ExperienceType.Commercial
            ? [
                  {
                      value: SpaceType.Boardroom,
                      label: 'Boardroom',
                  },
                  {
                      value: SpaceType.PrivateOffice,
                      label: 'Private Office',
                  },
              ]
            : [
                  {
                      value: SpaceType.Bedroom,
                      label: 'Bedroom',
                  },
                  {
                      value: SpaceType.LivingRoom,
                      label: 'Living Room',
                  },
                  {
                      value: SpaceType.Boardroom,
                      label: 'Boardroom',
                  },
                  {
                      value: SpaceType.Bathroom,
                      label: 'Bathroom',
                  },
                  {
                      value: SpaceType.DiningArea,
                      label: 'Dining Area',
                  },
                  {
                      value: SpaceType.GameRoom,
                      label: 'Game Room',
                  },
                  {
                      value: SpaceType.Garage,
                      label: 'Garage',
                  },
                  {
                      value: SpaceType.Kitchen,
                      label: 'Kitchen',
                  },
              ];

    const handleClose = () => {
        setShowAvailability(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: { width: '100%', maxWidth: 720 },
            }}
        >
            <FormProvider {...methods}>
                <DialogTitle>
                    {existingSpace
                        ? camelCaseToTitleCase(existingSpace.name)
                        : 'Space Configuration'}
                    <IconButton
                        onClick={handleClose}
                        size='small'
                        aria-label='close'
                        sx={{ color: 'text.primary' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <form id='space-modal-form' onSubmit={onNestedSubmit}>
                        <DialogContentText
                            sx={{
                                mb: 3,
                                fontWeight: 700,
                                color: 'text.primary',
                                fontSize: '14px',
                            }}
                        >
                            Configure the selected space by specifying amenities
                            and features.
                        </DialogContentText>

                        <Collapse in={cancel}>
                            <Alert severity='warning'>
                                You have unsaved changes. Are you sure you want
                                to cancel?
                            </Alert>
                        </Collapse>
                        <Stack rowGap={1}>
                            {!existingSpace && (
                                <Grid
                                    container
                                    columnSpacing={5}
                                    rowSpacing={1}
                                >
                                    <Grid item container xs={12}>
                                        <Grid item xs={12}>
                                            <InputLabel required>
                                                Type
                                            </InputLabel>
                                            <FormSelect
                                                name='spaceType'
                                                control={control}
                                                options={typeOptions}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        mt: 0.5,
                                        px: 1.25,
                                        py: 1,
                                        borderRadius: 2,
                                        backgroundColor:
                                            theme.palette.custom
                                                .cardActionBackground,
                                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                    }}
                                >
                                    <Stack
                                        direction={{
                                            xs: 'column',
                                            sm: 'row',
                                        }}
                                        spacing={1.25}
                                        alignItems={{
                                            xs: 'flex-start',
                                            sm: 'center',
                                        }}
                                        justifyContent='space-between'
                                    >
                                        <Stack
                                            direction='row'
                                            spacing={1}
                                            alignItems='center'
                                            flexWrap='wrap'
                                        >
                                            <FormSwitch
                                                name='isAvailable'
                                                control={control}
                                                label='Space currently available'
                                            />
                                        </Stack>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                width: {
                                                    xs: '100%',
                                                    sm: 'auto',
                                                },
                                            }}
                                        >
                                            {isAvailable && (
                                                <CustomButton
                                                    onClick={() =>
                                                        setShowAvailability(
                                                            !showAvailability,
                                                        )
                                                    }
                                                    startIcon={
                                                        <CalendarMonthOutlinedIcon />
                                                    }
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '12px',
                                                        px: 1.75,
                                                        py: 0.9,
                                                        borderRadius: 999,
                                                        boxShadow: 'none',
                                                        whiteSpace: 'nowrap',
                                                        width: {
                                                            xs: '100%',
                                                            sm: 'auto',
                                                        },
                                                    }}
                                                    variant='contained'
                                                    color='primary'
                                                >
                                                    {showAvailability
                                                        ? 'Hide calendar'
                                                        : 'Block out dates'}
                                                </CustomButton>
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            </Grid>

                            <AutoHeightTransition durationMs={400}>
                                {isAvailable && (
                                    <SpaceAvailability
                                        showAvailability={showAvailability}
                                    />
                                )}
                            </AutoHeightTransition>

                            {spaceType === SpaceType.Bedroom && (
                                <BedroomForm control={control} />
                            )}
                            {spaceType === SpaceType.GameRoom && (
                                <GameRoomForm control={control} />
                            )}
                            {spaceType === SpaceType.Garage && (
                                <GarageForm control={control} />
                            )}
                            {spaceType === SpaceType.DiningArea && (
                                <DiningAreaForm control={control} />
                            )}
                            {spaceType === SpaceType.Kitchen && (
                                <KitchenForm control={control} />
                            )}
                            {spaceType === SpaceType.LivingRoom && (
                                <LivingRoomForm control={control} />
                            )}
                            {spaceType === SpaceType.Bathroom && (
                                <BathroomForm control={control} />
                            )}
                            {spaceType === SpaceType.Boardroom && (
                                <BoardRoomForm control={control} type={type} />
                            )}
                            {spaceType === SpaceType.PrivateOffice && (
                                <PrivateOfficeForm type={subType} />
                            )}
                        </Stack>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button
                        variant='contained'
                        type='submit'
                        form='space-modal-form'
                        disabled={!isFormDirty}
                    >
                        Save
                    </Button>
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
};

export default SpaceModal;
